import React from 'react';
import PropTypes from 'prop-types';

import settingsReader from '../../store/readHelpers/SettingsReader';
import responseReader from '../../store/readHelpers/ResponseReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import { t } from '../../services/i18nService';
import navigationService from '../../services/NavigationService';
import scrollingService from '../../services/ScrollingService';
import styles from './tableOfContents.scss';
import questionSelectionService from '../../services/QuestionSelectionService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';

class TOCSection extends React.Component {
    static propTypes = {
        currentlyShown: PropTypes.array.isRequired,
        flaggedItems: PropTypes.array.isRequired,
        allowNavigation: PropTypes.bool.isRequired,
        section: PropTypes.object,
        settings: PropTypes.object.isRequired,
        sectionIndex: PropTypes.number,
        ui: PropTypes.object,
    };

    getQuestionNumberCellChildElement = (question, isDocument, isForm, isAttemptedQuestion, showTitles, allowNavigation, clickHandler) => {
        const isContentBlock = question.questionNumberingInSection !== undefined && question.questionNumberingInSection.length === 0;
        const notAQuestion = isDocument || isForm || isContentBlock;
        let attemptedText;

        if (notAQuestion) {
            attemptedText = '';
        } else if (isAttemptedQuestion) {
            attemptedText = t('generic.attempted');
        } else {
            attemptedText = t('generic.unattempted');
        }

        if (allowNavigation) {
            return (
                <button className={styles.accessibilityButton} onClick={clickHandler}>
                    <span className="sr-only">
                        { attemptedText }
                    </span>
                    {
                        notAQuestion ?
                            <React.Fragment >
                                <span className="sr-only">
                                    { t(`questionTypes.${question.objectType}`) }
                                </span>
                                <i className={`fa ${isForm ? 'fa-check-square-o' : 'fa-info'}`} aria-hidden="true" />
                            </React.Fragment>
                            :
                            <React.Fragment>
                                <span className="sr-only">
                                    { t('generic.questionNumberPrefix') }
                                </span>
                                { question.number }
                            </React.Fragment>
                    }
                    { showTitles &&
                        <span className="sr-only">
                            { question.title }
                        </span>
                    }
                </button>
            );
        }

        return (
            <React.Fragment>
                { notAQuestion
                    ? <i className={`fa ${isForm ? 'fa-check-square-o' : 'fa-info'}`} aria-hidden="true" />
                    : question.number
                }
            </React.Fragment>
        );
    };

    getTdQuestionNumberClasses = (isAttemptedQuestion, showTitles, allowNavigation) => {
        const tdQuestionNumberClasses = ['number'];
        if (isAttemptedQuestion) {
            tdQuestionNumberClasses.push('attempted');
        }
        if (!showTitles) {
            tdQuestionNumberClasses.push('number-only');
        }
        if (!allowNavigation) {
            tdQuestionNumberClasses.push(styles.noNavigationAllowed);
        }

        return tdQuestionNumberClasses;
    };

    getTdQuestionTypeClasses = (allowNavigation) => {
        const tdQuestionTypeClasses = [styles.objectType];
        if (!allowNavigation) {
            tdQuestionTypeClasses.push(styles.noNavigationAllowed);
        }
        return tdQuestionTypeClasses;
    };

    getQuestionRows = () => {
        const { currentlyShown, flaggedItems, allowNavigation, section, settings, ui } = this.props;
        const showTitles = settingsReader.isShowQuestionTitlesEnabled(settings);
        const showMaxMarks = settingsReader.isShowMaxMarksEnabled(settings);
        const flaggingEnabled = settingsReader.isFlaggingEnabled(settings);
        const atLeastOneSectionWithCandidateSelection = questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled();
        const sectionHasCandidateSelectionEnabled = questionSelectionService.sectionHasQuestionSelectionEnabled(section);
        const maxMarksMaxLen = currentlyShown.reduce((previousValue, questionPosition) => {
            const question = section.questions[questionPosition.questionIndex];
            if (!question.maxScore) {
                return previousValue;
            }

            const maxMarksStr = `${question.maxScore}`;
            return maxMarksStr.length > previousValue ? maxMarksStr.length : previousValue;
        }, 1);

        const maxMarksStyle = {
            width: `${maxMarksMaxLen * 0.7}em`,
        };

        return currentlyShown.map(({ questionIndex }) => {
            const question = section.questions[questionIndex];
            const isFlagged = flaggedItems.indexOf(`${question.contentRevisionId}`) > -1;
            const isDocument = questionReader.isDocument(question);
            const isForm = questionReader.isForm(question);
            const isAttemptedQuestion = responseReader.isQuestionAttempted(question);
            const tdQuestionNumberClasses = this.getTdQuestionNumberClasses(isAttemptedQuestion, showTitles, allowNavigation);
            const tdQuestionTypeClasses = this.getTdQuestionTypeClasses(allowNavigation);
            let clickHandler;

            const getCandidateSelectionStatus = () => {
                if (!sectionHasCandidateSelectionEnabled) {
                    return (
                        <span className="candidate-selection-status locked">
                            <i className="fa fa-2x fa-check" aria-hidden="true" />
                            <i className="fa fa-lock" aria-hidden="true" />
                            <span className="status-text">{t('table.candidateSelectionNotAvailable')}</span>
                        </span>
                    );
                }

                const selectorValue = questionSelectionService.getSelectionForQuestion(question.contentRevisionId);
                let classes = 'question';

                if (selectorValue === questionSelectionService.selectorOptions.YES) {
                    classes = 'check';
                } else if (selectorValue === questionSelectionService.selectorOptions.NO) {
                    classes = 'times warning';
                }

                return (
                    <span className="candidate-selection-status">
                        <i className={`fa fa-2x fa-${classes}`} aria-hidden="true" />
                        <span className="status-text">{t(`table.candidateSelectionValue.${selectorValue}`)}</span>
                    </span>
                );
            };

            if (navigationService.isOnAllView()) {
                clickHandler = () => scrollingService.scrollToQuestion(question.contentRevisionId, 'appContentContainer');
            } else if (allowNavigation) {
                clickHandler = () => navigationService.navigateToQuestion(question.contentRevisionId, section.id);
            }

            return (
                <tr
                    className={isFlagged ? styles.flagged : ''}
                    key={questionIndex}
                    onClick={clickHandler}
                >

                    <td aria-label={t('table.headerNumber')} className={tdQuestionNumberClasses.join(' ')}>
                        {
                            this.getQuestionNumberCellChildElement(question, isDocument, isForm,
                                isAttemptedQuestion, showTitles, allowNavigation, clickHandler)
                        }
                    </td>

                    { showTitles &&
                        <td aria-label={t('table.headerQuestionTitle')} className="title">
                            {showTitles ? question.title : null}
                        </td>
                    }

                    { showMaxMarks &&
                        <td aria-label={t('table.headerMaxMarks')} className={`${styles.centerContent} maxMarks`}>
                            <span className={styles.rightAlignWithin} style={maxMarksStyle}>
                                {
                                    ui.variables.question__showMarksInBrackets && question.maxScore ?
                                        `[${question.maxScore}]`
                                        : question.maxScore
                                }
                            </span>
                        </td>
                    }

                    <td className={tdQuestionTypeClasses.join(' ')} aria-label={t('table.headerQuestionType')}>
                        {t(`questionTypes.${question.objectType}`)}
                    </td>

                    { atLeastOneSectionWithCandidateSelection &&
                        <td aria-label={t('table.headerQuestionCandidateSelection')}>
                            {getCandidateSelectionStatus()}
                        </td>
                    }

                    { flaggingEnabled &&
                        <td aria-label={t('table.headerQuestionFlagged')} className={`${styles.centerContent} flag`}>
                            { isFlagged && <i className="fa fa-bookmark" aria-hidden="true" /> }

                            <span className="sr-only">
                                { isFlagged ? t('testOverview.flagged') : t('testOverview.notFlagged') }
                            </span>
                        </td>
                    }
                </tr>
            );
        });
    };

    getHeaderRow = () => {
        const { settings, ui } = this.props;
        const showFlaggedText = ui.variables.toc__showFlaggedText;
        const showTitles = settingsReader.isShowQuestionTitlesEnabled(settings);
        const showMaxMarks = settingsReader.isShowMaxMarksEnabled(settings);
        const flaggingEnabled = settingsReader.isFlaggingEnabled(settings);
        const showCandidateSelected = questionSelectionService.atLeastOneSectionWithQuestionSelectionEnabled();

        return (
            <tr key="-1">
                <th scope="col">{t('table.headerQuestionNo')}</th>

                { showTitles &&
                    <th scope="col">
                        {t('table.headerQuestionTitle')}
                    </th>
                }

                { showMaxMarks &&
                    <th className={styles.centerContent} scope="col">
                        {t('table.headerMaxMarks')}
                    </th>
                }

                <th className={styles.objectType} scope="col">
                    {t('table.headerQuestionType')}
                </th>

                { showCandidateSelected &&
                    <th scope="col">
                        {t('table.headerQuestionCandidateSelection')}
                    </th>
                }

                { flaggingEnabled &&
                    <th scope="col">
                        { showFlaggedText ?
                            t('testOverview.flagged')
                            : <span className="sr-only">{t('testOverview.flagged')}</span>
                        }
                    </th>
                }
            </tr>
        );
    };

    render() {
        const { section, sectionIndex, ui } = this.props;
        const sectionId = `section-${section.id}`;
        const showSectionTitle = settingsReader.isShowSectionEnabled();
        const tableHeaderOnEachTBody = ui.variables.toc__tableHeaderOnEachTBody && showSectionTitle;

        const showUnattemptedQuestions = marketplacePropertiesService.isSectionManualSelectionV2Enabled()
            && !questionSelectionService.areAllSectionsWithQuestionSelectionDisabled();
        const unattemptedQuestions = showUnattemptedQuestions && questionSelectionService.getMissedQuestionsAmountInSection(section);

        const questionRows = tableHeaderOnEachTBody ?
            [this.getHeaderRow()]
                .concat(this.getQuestionRows())
            : this.getQuestionRows();

        return (
            <tbody>
                { !tableHeaderOnEachTBody && sectionIndex === 0 && this.getHeaderRow() }

                { showSectionTitle &&
                    <tr>
                        <td colSpan="10" className={styles.sectionTitle} id={sectionId}>
                            <span className="sr-only">{t('generic.sectionNumberPrefix')}</span>
                            <span className={styles.sectionName}>{section.title}</span>
                            { showUnattemptedQuestions && (unattemptedQuestions > 0) &&
                                <span className={styles.unattemptedCaption}>
                                    {t('delivering.questionsLeftInSection', { left: unattemptedQuestions })}
                                </span>
                            }
                        </td>
                    </tr>
                }

                { questionRows }

                <tr className={styles.spacer}>
                    <td colSpan="10" />
                </tr>
            </tbody>
        );
    }
}

export default TOCSection;
